import { createFileRoute } from '@tanstack/react-router'
import { DocPage } from '../components/docs/DocPage'
import { Section, P, DefList, C } from '../components/docs/prose'

export const Route = createFileRoute('/docs/fire' as any)({ component: Fire })

const SECTIONS = [
  { id: 'what', title: 'What the planner does' },
  { id: 'reading', title: 'Reading the chart' },
  { id: 'sequence', title: 'Sequence-of-returns risk' },
  { id: 'levers', title: 'The levers' },
  { id: 'limits', title: 'What Monte Carlo cannot tell you' },
]

function Fire() {
  return (
    <DocPage
      path="/docs/fire"
      title="FIRE planner"
      lede={
        <>
          The planner runs your retirement plan five thousand times with
          randomised market returns, starting from your actual net worth. What
          comes out is a <em>distribution</em> of outcomes under your own
          assumptions &mdash; a range of possibility, not a prophecy.
        </>
      }
      sections={SECTIONS}
    >
      <Section id="what" title="What the planner does">
        <P>
          A plan is a set of assumptions: what life costs per year in
          today&apos;s dollars, what return you expect and how much it varies,
          inflation, a retirement date, and what you save monthly until then.
          The simulation compounds your current net worth month by month under
          those assumptions, adding contributions before retirement and
          withdrawing your (inflation-adjusted) expenses after it. Because
          returns are drawn randomly, each run tells a different story; five
          thousand of them together show the <em>shape</em> of what your
          assumptions imply.
        </P>
        <P>
          The success rate is the fraction of simulated futures in which the
          money never runs out within the horizon. It is a statement about the
          model, not the world: 90% means &ldquo;in 90% of runs consistent
          with these assumptions&rdquo;, not &ldquo;90% chance in real
          life&rdquo;.
        </P>
      </Section>

      <Section id="reading" title="Reading the chart">
        <DefList
          items={[
            [
              'Light band',
              '10th to 90th percentile: 80% of simulated futures land inside it.',
            ],
            [
              'Dark band',
              '25th to 75th percentile: the middle half of outcomes.',
            ],
            [
              'Solid line',
              'The median — half the runs end above it, half below. Not "the" outcome.',
            ],
            [
              'Dashed red line',
              'One concrete unlucky run (its ending ranks at the 10th percentile) — a story you can follow, unlike a band.',
            ],
          ]}
        />
        <P>
          The <C>Today&apos;s dollars</C> toggle deflates every value by your
          inflation assumption, so a 2050 balance reads in purchasing power
          you can actually picture. Future-dollar numbers always look more
          impressive; the real view is the honest one.
        </P>
      </Section>

      <Section id="sequence" title="Sequence-of-returns risk">
        <P>
          Two retirees can earn the same <em>average</em> return and end up in
          completely different places, because the order matters once you are
          withdrawing. A crash in your first retirement year forces you to
          sell cheap assets to eat, and the portfolio never recovers the
          shares it sold; the same crash twenty years in barely registers.
          Averages hide this &mdash; sequences reveal it. That is what the
          dashed unlucky path is for: it shows one plausible bad ordering,
          which no average or single &ldquo;expected&rdquo; projection can.
        </P>
      </Section>

      <Section id="levers" title="The levers">
        <P>
          The sliders re-simulate without saving the plan, so you can ask
          &ldquo;what actually moves my number?&rdquo; safely. The answers are
          usually humbling in a useful way: contribution changes act slowly
          but surely; the expected-return slider moves the chart dramatically
          precisely because it is the assumption you control least. Treat a
          plan that only works at 8% as a warning, not a strategy.
        </P>
      </Section>

      <Section id="limits" title="What Monte Carlo cannot tell you">
        <P>
          The model assumes returns are independent month to month and drawn
          from the same calm distribution forever. Real markets trend, crash,
          and mean-revert; real life adds a roof, a divorce, a diagnosis. The
          simulation also knows nothing about taxes, CPP/OAS, or the fact
          that people spend less at 85 than at 65.
        </P>
        <P>
          What it is good for: comparing YOUR options under consistent
          assumptions. Whether retiring two years later beats saving $500
          more a month is a question it answers well &mdash; both scenarios
          face the same simulated markets. Whether you will have exactly
          $1.4M in 2045 is a question nothing answers, and the bands are
          drawn wide precisely so the chart cannot pretend otherwise.
        </P>
      </Section>
    </DocPage>
  )
}
